import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getArticles } from '../../../../Requests/ArticleRequests'
import { getProject } from '../../../../Requests/ProjectRequests'
import { ArrowLeftIcon, PrinterIcon } from '@heroicons/react/24/outline'
import Loader from '../../../../Components/Loader'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

function FullArticleView() {
    const { project_uuid, articleId } = useParams()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [article, setArticle] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (project_uuid && articleId) {
            // First get the project to get the project ID
            getProject(project_uuid).then((projectRes) => {
                // Then get articles for that project
                return getArticles(projectRes.data.id)
            }).then((articlesRes) => {
                const foundArticle = articlesRes.data.articles.find(a => a.id === parseInt(articleId))
                if (foundArticle) {
                    // Check if all sections have been generated
                    const allSectionsGenerated = foundArticle.sections?.every(section => section.output)
                    if (allSectionsGenerated) {
                        setArticle(foundArticle)
                    } else {
                        // Redirect back to article detail if not all sections are generated
                        navigate(`/project/${project_uuid}/article/${articleId}`)
                    }
                } else {
                    navigate(`/project/${project_uuid}`)
                }
            }).catch((error) => {
                console.error('Error fetching article:', error)
                navigate(`/project/${project_uuid}`)
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [project_uuid, articleId, navigate])

    const handlePrint = () => {
        window.print()
    }

    const handleBack = () => {
        navigate(`/project/${project_uuid}/article/${articleId}`)
    }

    if (loading) {
        return (
            <div className='md:pl-64 pt-4 md:pt-12 w-full flex justify-center'>
                <div className='flex items-center justify-center h-64'>
                    <Loader />
                </div>
            </div>
        )
    }

    if (!article) {
        return (
            <div className='md:pl-64 pt-4 md:pt-12 w-full flex justify-center'>
                <div className='flex items-center justify-center h-64'>
                    <p>Article not found or not ready for viewing</p>
                </div>
            </div>
        )
    }

    return (

        <div className='md:pl-64 pt-4 md:pt-12 w-full flex justify-center print:pl-0 print:pt-0'>
            <div className='flex flex-col w-full justify-center items-center px-4 print:px-0'>
                {/* Header with navigation - hidden in print */}
                <div className='bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl mx-8 my-8'>
                    <div className="flex items-center justify-between p-6">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
                            >
                                <ArrowLeftIcon className="h-5 w-5" />
                                Back to Editor
                            </button>
                        </div>
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
                        >
                            <PrinterIcon className="h-5 w-5" />
                            Print Article
                        </button>
                    </div>
                </div>

                {/* Article Content */}
                <div className='bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl mx-8 my-8'>
                    <article className="prose prose-lg max-w-none p-8 print:p-12">
                        {/* Article Header */}
                        <header className="mb-12 text-center border-b pb-8">
                            <h1 className="text-4xl font-bold text-gray-900 mb-4">
                                {article.article_title}
                            </h1>
                            <div className="text-lg text-gray-600 leading-relaxed">
                                {article.article_details}
                            </div>
                            <div className="text-sm text-gray-500 mt-4">
                                Generated on {new Date(article.created_at).toLocaleDateString()}
                            </div>
                        </header>

                        {/* Article Sections */}
                        <div className="space-y-12">
                            {article.sections
                                ?.sort((a, b) => a.order_index - b.order_index)
                                .map((section, index) => (
                                    <section key={section.id} className="section">
                                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                                            {section.section_title}
                                        </h2>

                                        <div className="text-gray-800 leading-relaxed prose prose-lg max-w-none">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {section.output || ''}
                                            </ReactMarkdown>
                                        </div>

                                        {/* Add page break for print after each section except the last */}
                                        {index < article.sections.length - 1 && (
                                            <div className="print:page-break-after-always print:break-after-page"></div>
                                        )}
                                    </section>
                                ))
                            }
                        </div>

                        {/* Footer */}
                        <footer className="mt-16 pt-8 border-t text-center text-gray-500">
                            <p className="text-sm">
                                Article generated with {article.article_pages} pages • {article.sections?.length} sections
                            </p>
                            {article.input_artifacts && Array.isArray(article.input_artifacts) && article.input_artifacts.length > 0 && (
                                <p className="text-xs mt-2">
                                    Based on: {article.input_artifacts.join(', ')}
                                </p>
                            )}
                        </footer>
                    </article>
                </div>
            </div>

            {/* Print-specific styles */}
            <style jsx>{`
                @media print {
                    body {
                        font-family: 'Times New Roman', serif;
                        line-height: 1.6;
                    }
                    
                    .section {
                        orphans: 3;
                        widows: 3;
                    }
                    
                    h1, h2, h3 {
                        page-break-after: avoid;
                        orphans: 3;
                        widows: 3;
                    }
                    
                    @page {
                        margin: 2.5cm;
                        size: A4;
                    }
                    
                    .print\\:page-break-after-always {
                        page-break-after: always;
                    }
                    
                    .print\\:break-after-page {
                        break-after: page;
                    }
                }
            `}</style>
        </div>
    )
}

export default FullArticleView
